import { useLocation } from 'react-router-dom';
import { Hammer } from 'lucide-react';

import { EmptyState } from '@/components/ui/EmptyState';
import { PARENT_NAV, navKeyFromPath, navLabel } from '@/constants/navigation';
import { useAuthStore } from '@/features/auth/store/auth.store';
import { cn } from '@/lib/utils';
import { useSelectedChild } from '../store/child.store';
import { PARENT_PALETTE } from '../theme';

/**
 * A section of the parent panel that is on the menu but not built yet.
 *
 * The menu is the whole panel's map, so every entry on it has to land
 * somewhere: a link that 404s tells a parent their school's software is broken,
 * and a link left off the menu until its screen exists hides what is coming.
 * This says plainly which section it is and that it is on its way.
 *
 * Each section replaces this with its own page as it is built — one line in
 * `routes/index.tsx` per screen. When the last one lands, this file goes.
 */
export function ParentSectionPage() {
  const location = useLocation();
  const child = useSelectedChild();

  const key = navKeyFromPath(PARENT_NAV, location.pathname);
  const section = PARENT_NAV.find((option) => option.key === key) ?? PARENT_NAV[0];
  const featureNames = useAuthStore((state) => state.user?.featureNames);
  const label = navLabel(section, featureNames);
  const Icon = section.icon;

  return (
    <div className={cn('flex flex-col gap-5', PARENT_PALETTE)}>
      <header className="min-w-0">
        <h1 className="text-2xl font-bold tracking-tight text-ink">{label}</h1>
        {child && (
          <p className="mt-1 text-sm text-ink-2">
            For {child.fullName} in {child.className}.
          </p>
        )}
      </header>

      <div className="card">
        <EmptyState
          icon={<Icon className="h-5 w-5" />}
          title={`${label} is being built`}
          description="This section of your parent panel is not ready yet. The school office can answer anything you need from it in the meantime."
          action={
            <p className="inline-flex items-center gap-1.5 text-xs font-semibold text-ink-3">
              <Hammer className="h-3.5 w-3.5" aria-hidden />
              Coming soon
            </p>
          }
        />
      </div>
    </div>
  );
}
